import type { FlightCardData } from '@massilia/queries'

import { Button } from '@massilia/ui'
import { RotateCcw } from 'lucide-react'
import { useMemo } from 'react'

import { Route } from '@/routes/_booking/flights.results'
import { useFilters } from '@/utils/use-search-params'

import { type AirlineOption, CompanyFilter } from './company-filter'
import { DurationFilter } from './duration-filter'
import { getDurationBounds, getPriceBounds } from './filter-utils'
import { LuggageFilter } from './luggage-filter'
import { PriceFilter } from './price-filter'
import { StopsFilter } from './stops-filter'
import { TimeFilter } from './time-filter'

interface FlightFiltersProps {
  offers: FlightCardData[]
}

export function FlightFilters({ offers }: FlightFiltersProps) {
  const { filters, setFilters } = useFilters(Route.id)

  const priceBounds = useMemo(() => getPriceBounds(offers), [offers])
  const durationBounds = useMemo(() => getDurationBounds(offers), [offers])
  const availableAirlines = useMemo((): AirlineOption[] => {
    const airlinesMap = new Map<string, string>()
    offers.forEach((offer) => {
      if (!airlinesMap.has(offer.airlineCode)) {
        airlinesMap.set(offer.airlineCode, offer.airline)
      }
    })
    return Array.from(airlinesMap.entries())
      .map(([code, name]) => ({ name, code }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [offers])

  const hasActiveFilters = useMemo(() => {
    const isPriceFilterActive = !!(
      filters.filterPriceMin &&
      filters.filterPriceMax &&
      (filters.filterPriceMax > priceBounds.min || filters.filterPriceMin < priceBounds.max)
    )
    const isDurationFilterActive = !!(
      filters.filterDurationMin &&
      filters.filterDurationMax &&
      (filters.filterDurationMax > durationBounds.min ||
        filters.filterDurationMin < durationBounds.max)
    )
    const isTimeFilterActive = filters.filterTimeRanges
      ? filters.filterTimeRanges.length > 0
      : false
    const isAirlinesFilterActive = filters.filterAirlines
      ? filters.filterAirlines.length > 0
      : false

    return (
      isPriceFilterActive ||
      isDurationFilterActive ||
      isTimeFilterActive ||
      isAirlinesFilterActive ||
      filters.filterStops !== 'all' ||
      filters.filterLuggage !== '1'
    )
  }, [filters, priceBounds, durationBounds])

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">Filtres</h2>

      <div className="space-y-0">
        <CompanyFilter airlines={availableAirlines} offers={offers} />
        <LuggageFilter />
        <StopsFilter />
        <PriceFilter bounds={priceBounds} />
        <DurationFilter bounds={durationBounds} />
        <TimeFilter offers={offers} />
      </div>

      {/* Reset button */}
      <div className="flex flex-row justify-between border-t border-gray-200 pt-4">
        <Button
          className="w-full"
          disabled={!hasActiveFilters}
          onClick={() => {
            setFilters({
              filterAirlines: undefined,
              filterDurationMax: undefined,
              filterDurationMin: undefined,
              filterLuggage: undefined,
              filterPriceMax: undefined,
              filterPriceMin: undefined,
              filterStops: undefined,
              filterTimeRanges: undefined,
            })
          }}
          variant="outline"
        >
          <RotateCcw className="ml-2 h-4 w-4" />
          <span className="mr-2 py-2 text-sm font-medium text-wrap text-gray-900">
            Réinitialiser les filtres
          </span>
        </Button>
      </div>
    </div>
  )
}
